import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useSession, useUser, useClerk } from '@clerk/react';
import axiosInstance, { setClerkTokenGetter } from '../utils/axiosInstance';

export interface AppUser {
  user_id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar: string | null;
  score: number | null;
  displayName: string;
  clerkImageUrl: string | null;
}

interface AuthContextValue {
  user: AppUser | null;
  loading: boolean;
  logout: (sendMessage?: boolean) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { session } = useSession();
  const { isSignedIn, isLoaded: clerkLoaded, user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const loggingOut = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const fetchUserDetails = useCallback(async () => {
    try {
      const response = await axiosInstance.get('api/users/me/');
      const rawName =
        clerkUser?.firstName || response.data.first_name || response.data.username;
      const displayName = rawName
        ? rawName.charAt(0).toUpperCase() + rawName.slice(1)
        : '';
      const userData: AppUser = {
        ...response.data,
        email: clerkUser?.primaryEmailAddress?.emailAddress || response.data.email || '',
        displayName,
        clerkImageUrl: clerkUser?.imageUrl || null,
      };
      localStorage.setItem('userDetails', JSON.stringify(userData));
      setUser(userData);
    } catch (e) {
      console.error('Error fetching user details:', e);
    }
  }, [clerkUser]);

  const logout = useCallback(
    async (sendMessage = true) => {
      if (loggingOut.current) return;
      loggingOut.current = true;
      setLoading(true);
      localStorage.removeItem('userDetails');
      setUser(null);

      if (sendMessage) {
        channelRef.current?.postMessage({ type: 'logout' });
      }

      try {
        await signOut();
      } catch (e) {
        console.error('Error signing out of Clerk:', e);
      }
    },
    [signOut]
  );

  // Token-getter: only depends on session. Keep separate from fetch-user effect
  // so cleanup doesn't race with in-flight fetches and nullify the token.
  useEffect(() => {
    if (session) {
      setClerkTokenGetter(() => session.getToken());
    } else {
      setClerkTokenGetter(null);
    }
    return () => setClerkTokenGetter(null);
  }, [session]);

  // Fetch user details once the Clerk session is ready.
  useEffect(() => {
    if (!clerkLoaded) return;

    if (isSignedIn && session && !user && !loggingOut.current) {
      fetchUserDetails().finally(() => setLoading(false));
    } else if (!isSignedIn && !session) {
      loggingOut.current = false;
      setUser(null);
      setLoading(false);
    }
  }, [clerkLoaded, isSignedIn, session, user, fetchUserDetails]);

  // Cross-tab sync. ONE BroadcastChannel instance, stored in a ref, reused by logout().
  // Creating a new instance inside logout() would cause the listener in the same tab
  // to receive the message and trigger signOut a second time.
  useEffect(() => {
    const channel = new BroadcastChannel('auth');
    channelRef.current = channel;
    channel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'logout') {
        logout(false);
      } else if (event.data?.type === 'login' && session) {
        fetchUserDetails();
      }
    };
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [logout, fetchUserDetails, session]);

  return (
    <AuthContext.Provider value={{ user, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
